import { createClass as _createClass, toConsumableArray as _toConsumableArray, classCallCheck as _classCallCheck, defineProperty as _defineProperty } from '../../../_virtual/_rollupPluginBabelHelpers.js';

/**
 * Copyright 2021, IntraLinks, Inc. All rights reserved.
 */
var MenuNav = /*#__PURE__*/function () {
  function MenuNav(config) {
    _classCallCheck(this, MenuNav);
    /**
     * @property {HTMLElement}
     * container element with role=menu
     * typically a UL element
     */
    _defineProperty(this, "itemsContainer", null);
    /**
     * @property {HTMLElement}
     * the item targeted for possible selection
     * via arrow key navigation
     */
    _defineProperty(this, "activeItem", null);
    /**
     * @property {array}
     * the keyboard navigable descendants of the itemsContainer
     * which must have a role of menuitem, menuitemcheckbox, or menuitemradio
     * non-navigable valid sibling roles include none (for headings), and separator
     */
    _defineProperty(this, "items", []);
    /**
     * @property {number}
     * the index of the activeItem above within the items array above
     */
    _defineProperty(this, "activeIndex", 0);
    /**
     * @property {object}
     */
    _defineProperty(this, "keyCodes", {
      DOWN: 40,
      END: 35,
      ENTER: 13,
      ESCAPE: 27,
      HOME: 36,
      LEFT: 37,
      PAGE_DOWN: 34,
      PAGE_UP: 33,
      RIGHT: 39,
      SPACE: 32,
      TAB: 9,
      UP: 38
    });
    this.config = config || {};
    this.config.activeClass = this.config.activeClass || 'active-item';
    // allow below methods to be called externally with context
    this.keyHandler = this.keyHandler.bind(this);
    this.nav = this.nav.bind(this);
  }
  return _createClass(MenuNav, [{
    key: "getItems",
    value:
    /**
     * identifies the items-container
     * (typically an unordered list element)
     * and menu items
     * intended to be called with each focus event since elements may rerender
     * @param {Event} e native or synthetic focus event
     * @returns {array} collection of menu item elements
     */
    function getItems(e) {
      var _this = this;
      this.itemsContainer = e.currentTarget;
      var itemsSelector = '[role=menuitem], [role=menuitemradio], [role=menuitemcheckbox]';
      var randomHash = (Math.random() * 10 * 1e16).toString(36);
      this.itemsContainer.id = this.itemsContainer.id || "menu-".concat(randomHash);
      var items = _toConsumableArray(this.itemsContainer.querySelectorAll(itemsSelector)).map(function (originalItem, index) {
        var item = originalItem;
        // menu items must have ID's
        // in order for us to use the aria-activedescendant technique
        item.id = item.id || "".concat(_this.itemsContainer.id, "-item-").concat(index);
        return item;
      });
      return items;
    }

    /**
     * changes the menu's active item
     *
     * @param {number} index index of item to activate
     * @param {boolean} expanded if menu is expanded
     * @param {object} e native JS event
     * @returns {htmlElement} active item
     */
  }, {
    key: "nav",
    value: function nav(index, e) {
      var _this$itemsContainer;
      var expanded = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : !!((_this$itemsContainer = this.itemsContainer) !== null && _this$itemsContainer !== void 0 && _this$itemsContainer.clientHeight);
      if (e) {
        this.items = this.getItems(e);
        e.preventDefault();
        e.stopPropagation();
      }
      var item = this.items[index];

      // do not allow item navigation while menu is hidden
      if (item && expanded) {
        this.markUpActiveItem(item);
        this.activeItem = item;
        this.activeIndex = index;
      }
      return item;
    }

    /**
     * keydown handler listening on the items container
     * typically a ul element
     * @param {object} e native JS event or synthetic react event
     * @returns {htmlElement} active item
     */
  }, {
    key: "keyHandler",
    value: function keyHandler(e) {
      var matchIndex;
      switch (e.keyCode) {
        case this.keyCodes.UP:
          return this.nav(this.activeIndex - 1, e);
        case this.keyCodes.DOWN:
          return this.nav(this.activeIndex + 1, e);
        case this.keyCodes.HOME:
          return this.nav(0, e);
        case this.keyCodes.END:
          return this.nav(this.items.length - 1, e);
        case this.keyCodes.SPACE:
          return this.activeItem;
        case this.keyCodes.ESCAPE:
          if (this.config.escapeCallBack) {
            e.stopPropagation(); // do not close modals from an escape key press in a menu
            return this.config.escapeCallBack(e);
          }
          return this.activeItem;
        case this.keyCodes.ENTER:
          this.activeItem && this.selectItem(this.activeItem);
          return this.activeItem;
        default:
          matchIndex = this.itemMatches(e.key, true, e);
          if (matchIndex !== undefined) {
            return this.nav(matchIndex);
          }
      }
      return this.activeItem;
    }

    /**
     * processes the activation of a menu item
     * navigated to via the keyboard for possible selection
     * similar to a hover state, but using keyboard
     * @param {htmlEelement} item menu item
     * @returns {htmlElement} menu item provided
     */
  }, {
    key: "markUpActiveItem",
    value: function markUpActiveItem(item) {
      var _this2 = this;
      if (item && item.parentNode) {
        _toConsumableArray(this.itemsContainer.getElementsByClassName(this.config.activeClass)).forEach(function (otherItem) {
          return otherItem.classList.remove(_this2.config.activeClass);
        });
        item.classList.add(this.config.activeClass);
        if (item.scrollIntoViewIfNeeded) {
          // scrollIntoView() can cause scrollbars to jump unnecessarily in some browsers
          // utilize the scrollIntoViewIfNeeded method if it is supported
          // other users may need to scroll manually after navigating with arrow keys
          // in browsers that don't support scrollIntoViewIfNeeded (currently Firefox and IE11)
          item.scrollIntoViewIfNeeded();
        }
      }
      this.itemsContainer.setAttribute('aria-activedescendant', item && item.id);
      return item;
    }

    /**
     * selects a menu item
     * where appropriate sets aria-checked attribute and resets it on siblings
     * and clicks the item
     * @param {htmlEelement} item menu item element or index
     * @param {boolean} click true if we should click
     * @returns {htmlElement} menu item provided
     */
  }, {
    key: "selectItem",
    value: function selectItem(item) {
      if (item) {
        var role = item.getAttribute('role');
        var disabled = item.getAttribute('disabled');
        if (disabled) {
          return item;
        }
        // menuitem role does not retain selection via aria-checked
        // menuitemcheckbox and menuitemradio do
        if (role === 'menuitemradio') {
          _toConsumableArray(this.itemsContainer.querySelectorAll('[aria-checked=true]')).forEach(function (otherItem) {
            otherItem.setAttribute('aria-checked', 'false');
          });
        }
        if (role === 'menuitemcheckbox' || role === 'menuitemradio') {
          item.setAttribute('aria-checked', 'true');
        }
        item.click();
      }
      return item;
    }

    /**
     * matches item content to a string
     * @param {string} matchString string to match
     * @param {boolean} initial true if we should match initial letter only
     * @param {Event} e keydown event
     * @returns {number | undefined} index of the matching item if there is one
     */
  }, {
    key: "itemMatches",
    value: function itemMatches(rawMatchString, initial, e) {
      this.items = this.getItems(e);
      var matchString = rawMatchString.trim().toLowerCase();
      var itemString;
      for (var i = (this.activeIndex || 0) + 1, stop = this.items.length, match; i < stop; i++) {
        if (this.items[i]) {
          itemString = this.items[i].textContent.trim().toLowerCase();
          if (initial) {
            itemString = itemString.substr(0, 1);
          }
          if (itemString === matchString) {
            return i;
          }
        }
      }
      for (var j = 0, _stop = this.activeIndex, _match; j <= _stop; j++) {
        if (this.items[j]) {
          itemString = this.items[j].textContent.trim().toLowerCase();
          if (initial) {
            itemString = itemString.substr(0, 1);
          }
          if (itemString === matchString) {
            return j;
          }
        }
      }
      return null;
    }
  }]);
}();

export { MenuNav as default };
